import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useAuthStore } from '@/store/auth'
import { counts, obnovitPocty, ukonciSledovanie, zacniSledovanie } from '@/composables/useNotifikacie'
import { oddelenieLabel } from '@/utils/oddelenia'

/*
 * Navigácia appky pre ľavý panel aj mobilné menu. Počty v menu a upozornenia (zvonček) sú zdieľaný stav
 * z useNotifikacie: načítavajú sa spolu raz za 30 s bez ohľadu na to, koľko komponentov navigáciu práve zobrazuje.
 */

/* Zbalenie ľavého panela (⌘B / Ctrl+B), pamätá sa v prehliadači */
const COLLAPSE_KEY = 'mania.sidebar.collapsed'
function readCollapsed() {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1'
  } catch (e) {
    return false
  }
}
export const sidebarCollapsed = ref(readCollapsed())
watch(sidebarCollapsed, (value) => {
  try {
    localStorage.setItem(COLLAPSE_KEY, value ? '1' : '0')
  } catch (e) {
    // súkromný režim prehliadača — stav sa len nezapamätá
  }
})
export function toggleSidebar() {
  sidebarCollapsed.value = !sidebarCollapsed.value
}

/* Mobilná zásuvka s celou navigáciou */
export const mobileDrawerOpen = ref(false)

/* Dialóg „Nahlásiť chybu“ (FeedbackWidget) — otvára sa z položky v ľavom paneli */
export const feedbackDialogOpen = ref(false)
export function openFeedbackDialog() {
  feedbackDialogOpen.value = true
}

const ROLE_LABELS = {
  admin: 'Administrátor',
  manager: 'Manažér',
  manazer: 'Manažér',
  manazment: 'Manažment',
  prevadzka: 'Prevádzka',
}

export function useAppNavigation({ poll = true } = {}) {
  const authStore = useAuthStore()
  const isAdmin = computed(() => authStore.user?.rola === 'admin')

  const groups = computed(() => {
    const list = [
      {
        key: 'main',
        items: [
          { title: 'Prehľad', icon: 'mdi-home', to: '/', exact: true },
          { title: 'Úlohy', icon: 'mdi-clipboard-check', to: '/ulohy', badge: counts.tasks },
          { title: 'Novinky', icon: 'mdi-newspaper', to: '/novinky', badge: counts.news },
        ],
      },
      {
        key: 'info',
        label: 'Informácie',
        items: [
          { title: 'Kontakty', icon: 'mdi-card-account-phone', to: '/kontakty' },
          // Zapnuté: regionálni vedúci nahrávajú smernice (požiadavka z tabuľky hlásení, bod 6)
          { title: 'Smernice', icon: 'mdi-script-text-outline', to: '/smernice' },
          { title: 'Interné príkazy', icon: 'mdi-file-sign', to: '/prikazy', exact: true },
          // Rozpracované príkazy na pripomienkovanie - len pre tých, čo príkazy spravujú
          ...(authStore.mozeNahravatDokumenty
            ? [{ title: 'Koncepty', icon: 'mdi-file-document-edit-outline', to: '/prikazy/koncepty', sub: true }]
            : []),
          { title: 'Tovaroznalectvo', icon: 'mdi-glasses', to: '/tovaroznalectvo', disabled: true },
        ],
      },
      {
        key: 'services',
        label: 'Služby',
        items: [
          { title: 'Požiadavky', icon: 'mdi-inbox', to: '/poziadavky', badge: counts.requests },
          { title: 'BonusCard', icon: 'mdi-credit-card-outline', to: '/passmachine' },
          { title: 'Aktuálne akcie', icon: 'mdi-bullhorn', to: '/marketing' },
        ],
      },
      {
        key: 'docs',
        label: 'Dokumenty a kalendár',
        items: [
          { title: 'Dokumenty', icon: 'mdi-folder', to: '/dokumenty' },
          { title: 'Kalendár', icon: 'mdi-calendar', to: '/kalendar' },
        ],
      },
      {
        key: 'feedback',
        label: 'Spätná väzba',
        items: [
          { title: 'Nápady a podnety', icon: 'mdi-lightbulb', to: '/feedback', badge: counts.feedback },
          // Bez `to` — položka neprechádza na stránku, len otvorí dialóg
          { title: 'Nahlásiť chybu', icon: 'mdi-message-alert-outline', action: openFeedbackDialog },
        ],
      },
      {
        key: 'help',
        label: 'Pomoc',
        items: [{ title: 'Manuál', icon: 'mdi-book-open-page-variant', to: '/manual', exact: true }],
      },
    ]
    if (isAdmin.value) {
      list.push({
        key: 'admin',
        label: 'Administrácia',
        items: [
          { title: 'Používatelia', icon: 'mdi-account-group', to: '/admin/users' },
          { title: 'Predajne', icon: 'mdi-store', to: '/admin/predajne' },
          { title: 'Správa manuálu', icon: 'mdi-book-edit', to: '/manual/admin' },
          { title: 'Feedback', icon: 'mdi-bug-outline', to: '/admin/feedback', badge: counts.systemFeedback },
          { title: 'Štatistiky', icon: 'mdi-chart-box', to: '/statistiky' },
        ],
      })
    } else if (authStore.jeVedenie) {
      // Vedenie (Manažment / Riaditeľ) má prístup k štatistikám, nie k administrácii
      list.push({
        key: 'vedenie',
        label: 'Vedenie',
        items: [{ title: 'Štatistiky', icon: 'mdi-chart-box', to: '/statistiky' }],
      })
    }
    return list
  })

  const user = computed(() => authStore.user)
  const userName = computed(() => {
    const u = authStore.user
    return u ? `${u.meno || ''} ${u.priezvisko || ''}`.trim() || u.email : 'Používateľ'
  })
  const userInitials = computed(() => {
    const u = authStore.user
    if (!u) return '?'
    const initials = `${(u.meno || '').charAt(0)}${(u.priezvisko || '').charAt(0)}`.toUpperCase()
    return initials || (u.email || '?').charAt(0).toUpperCase()
  })
  // Pod menom: rola, pri kancelárskych účtoch radšej oddelenie (napr. „Regionálny vedúci“, „Marketing“)
  const userRole = computed(() => {
    const u = authStore.user
    if (!u) return ''
    if (u.oddelenie && !['admin', 'prevadzka'].includes(u.rola)) return oddelenieLabel(u.oddelenie)
    return ROLE_LABELS[u.rola] || 'Zamestnanec'
  })

  if (poll) {
    onMounted(zacniSledovanie)
    onBeforeUnmount(ukonciSledovanie)
  }

  return { groups, counts, user, userName, userInitials, userRole, isAdmin, refreshCounts: obnovitPocty }
}
